import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzCheckboxComponent } from 'ng-zorro-antd/checkbox';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzCardComponent } from 'ng-zorro-antd/card';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NgxPermissionsModule } from 'ngx-permissions';
import { InputModule } from '@beautyapp/form';
import {
  AvailablePackageService,
  calculateDiscountPercent,
  EmployeeProcedureService,
  secondsToTimeStr,
  ServiceService,
  timeStrToSeconds,
} from '@beautyapp/core';
import { firstValueFrom } from 'rxjs';
import {
  AppointmentProductsComponent,
  AppointmentSpecServicesComponent,
} from '../../../../../booking-modal/components';
import { ChooseServiceComponent } from '../../../choose-service/choose-service.component';
import {
  AdditionalEmployeeValue,
  BookingFormValue,
  BookingServiceInitialData,
} from '../../types';
import { NzTagModule } from 'ng-zorro-antd/tag';
import {
  seedAutoFixExpense,
  shouldTrackProductExpense,
} from '../../../seed-auto-fix-expense';

/**
 * Custom validator to ensure time value is greater than zero
 */
function ValidateTime(control: AbstractControl): { [key: string]: any } | null {
  if (control.value && timeStrToSeconds(control.value) <= 0) {
    return { minTimeRequired: true };
  }
  return null;
}

@Component({
  selector: 'beautyapp-booking-service-form',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    NzButtonComponent,
    NzCheckboxComponent,
    NzIconDirective,
    NzCardComponent,
    NzSelectModule,
    NzInputNumberModule,
    NgxPermissionsModule,
    InputModule,
    AppointmentProductsComponent,
    AppointmentSpecServicesComponent,
    ChooseServiceComponent,
    NzTagModule,
  ],
  templateUrl: './booking-service-form.component.html',
  styleUrl: './booking-service-form.component.scss',
})
export class BookingServiceFormComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private serviceService = inject(ServiceService);
  private employeeServiceService = inject(EmployeeProcedureService);

  // ============ INPUTS ============

  /** List of employees for additional employees dropdown */
  employees = input<any[]>([]);

  /** Initial data for editing */
  initialData = input<BookingServiceInitialData | null>(null);

  /** Current employee data */
  employee = input.required<any>();

  /** Room data (for multi-employee support) */
  room = input<any | null>(null);

  /** Booking status */
  bookingStatus = input<string | undefined>(undefined);

  /** Whether salary fields are readonly (for Employee role) */
  salaryReadonly = input<boolean>(false);

  /** Booking ID for add mode */
  bookingId = input<string | null>(null);

  /** User ID for package service lookup */
  userId = input<string | null>(null);

  /**
   * Booking date used by the package dropdown to pre-filter out packages
   * that would expire before then (#2655). Optional — when null the BE
   * falls back to today.
   */
  bookingDate = input<Date | string | null>(null);

  /** View-only mode - disables all editing */
  viewOnly = input<boolean>(false);

  // ============ OUTPUTS ============

  /** Emits when form value changes */
  formValueChange = output<BookingFormValue>();

  /** Emits when form validity changes */
  formValidChange = output<boolean>();

  /** Emits the selected service */
  serviceChange = output<any>();

  /** Emits service products */
  serviceProductsChange = output<any[]>();

  /** Emits spec services */
  specServicesChange = output<any[]>();

  // ============ STATE ============

  /** Whether service selection is needed */
  readonly needChooseService = signal(true);

  /** Currently selected service */
  readonly service = signal<any | null>(null);

  /** Products associated with the service */
  readonly serviceProducts = signal<any[]>([]);

  /** Special services associated with the booking */
  readonly serviceSpecs = signal<any[]>([]);

  /** Initialization complete flag */
  private initialized = signal(false);

  // ============ COMPUTED ============

  /** Check if room allows multiple employees */
  readonly roomAllowsMultipleEmployees = computed(
    () => this.room()?.canWorkMultipleEmployees || false
  );

  /** Gets the sale price from the form */
  get salePrice() {
    return this.form.get('salePrice')?.value;
  }

  /** Product cost + additional cost, for display only. */
  get totalExpense(): number {
    const productExpense = +(this.form.get('expense')?.value || 0);
    const additionalExpense = +(this.form.get('additionalExpense')?.value || 0);
    return productExpense + additionalExpense;
  }

  /** Determines if salary percent from price option should be enabled */
  get isEnableSalaryPercentFromPrice() {
    return (
      this.salePrice &&
      this.salePrice > 0 &&
      this.form.get('salaryPercent')?.value
    );
  }

  // ============ FORM ============

  form = new FormGroup({
    bookingId: new FormControl<string | null>(null),
    serviceId: new FormControl<string | null>(null, Validators.required),
    time: new FormControl<string | null>(null, [
      Validators.required,
      ValidateTime,
    ]),
    price: new FormControl<number | null>(null, Validators.required),
    salePrice: new FormControl<number | null>(null),
    salePercent: new FormControl<number | null>(null),
    employeeId: new FormControl<string | null>(
      { value: null, disabled: true },
      Validators.required
    ),
    salaryFixed: new FormControl<number | null>(null),
    // beaut-424: a share of a price cannot exceed the price. Without this the
    // field took any number and the backend multiplied it raw —
    // (price * percent) / 100 — so 5,000,000% on a 1,000 GEL procedure produced
    // a 50,000,000 GEL salary. The API now rejects it too (see the backend PR);
    // this stops the operator reaching that error in the first place.
    salaryPercent: new FormControl<number | null>(null, [
      Validators.min(0),
      Validators.max(100),
    ]),
    description: new FormControl<string | null>(null),
    // Product cost — seeded from the service products, then editable: a recipe
    // states a nominal amount, but what a procedure actually consumes varies.
    // Re-seeded whenever the service or its products change.
    expense: new FormControl<number | null>(null),
    // Additional cost — manually entered; never touched by the product-cost
    // recalculation logic below, and sent to the backend unchanged (beaut-240).
    additionalExpense: new FormControl<number | null>(null),
    quantity: new FormControl(1, { nonNullable: true }),
    salaryPercentFromPrice: new FormControl(false, { nonNullable: true }),
    minutes: new FormControl(0, { nonNullable: true }),
    additionalEmployees: new FormArray<FormGroup>([]),
  });

  get additionalEmployeesForm() {
    return this.form.get('additionalEmployees') as FormArray;
  }

  constructor() {
    this.setupFormSubscriptions();
  }

  async ngOnInit() {
    await this.initialize();
  }

  // ============ INITIALIZATION ============

  private async initialize(): Promise<void> {
    const data = this.initialData();
    const emp = this.employee();

    if (data) {
      // Edit mode - initialize from existing data
      await this.initializeFromData(data);
    } else {
      // Add mode
      this.needChooseService.set(true);
      if (emp) {
        this.form.get('employeeId')?.setValue(emp.id, { emitEvent: false });
        this.form.patchValue(
          {
            bookingId: this.bookingId(),
            salaryFixed: null,
            salaryPercent: emp.outputPercent,
          },
          { emitEvent: false }
        );
      }
    }

    this.initialized.set(true);
    this.emitFormValue();
  }

  private async initializeFromData(
    data: BookingServiceInitialData
  ): Promise<void> {
    this.needChooseService.set(false);

    // Load service
    if (data.service?.id || data.serviceId) {
      await this.loadService(data.service?.id || data.serviceId);
    }

    // Set specs and products (map products to ensure `price` field is calculated)
    this.serviceSpecs.set(data.specServices || []);
    this.serviceProducts.set(
      this.mapServiceProducts(data.serviceProducts || [])
    );

    // Patch form values
    this.form.patchValue(
      {
        bookingId: data.bookingId || null,
        serviceId: data.service?.id || data.serviceId,
        time: data.time,
        price: +data.price,
        salePrice: +data.salePrice,
        salePercent:
          +data.salePrice > 0
            ? +calculateDiscountPercent(+data.price, +data.salePrice)
            : null,
        salaryFixed: +data.salaryFixed,
        salaryPercent: +data.salaryPercent,
        salaryPercentFromPrice: data.salaryPercentFromPrice,
        description: data.description || null,
        expense: +data.expence,
        additionalExpense: +(data.additionalExpence || 0),
        quantity: data.quantity,
        minutes: timeStrToSeconds(data.time),
      },
      { emitEvent: false }
    );

    // Set employeeId separately (disabled control)
    this.form
      .get('employeeId')
      ?.setValue(this.employee()?.id, { emitEvent: false });

    // Load additional employees
    if (data.additionalEmployees?.length) {
      this.loadAdditionalEmployees(data.additionalEmployees);
    }
  }

  private async loadService(serviceId: string): Promise<void> {
    if (!serviceId) return;

    try {
      const service = await firstValueFrom(
        this.serviceService.getById(serviceId)
      );
      this.service.set(service);
      this.serviceChange.emit(service);

      if (service.serviceProducts?.length > 0) {
        this.serviceProducts.set(
          this.mapServiceProducts(service.serviceProducts)
        );
      }
    } catch {
      this.service.set(null);
    }
  }

  private loadAdditionalEmployees(employees: AdditionalEmployeeValue[]): void {
    employees.forEach((emp) => {
      this.additionalEmployeesForm.push(
        new FormGroup({
          employeeId: new FormControl(emp.employeeId),
          salaryFixed: new FormControl(emp.salaryFixed),
          // beaut-424: same ceiling as the main employee's share.
          salaryPercent: new FormControl(emp.salaryPercent, [
            Validators.min(0),
            Validators.max(100),
          ]),
        })
      );
    });
  }

  // ============ FORM SUBSCRIPTIONS ============

  private setupFormSubscriptions(): void {
    // Time changes - calculate minutes and handle MINUTE service type
    this.form
      .get('time')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((time) => {
        if (!time || !this.initialized()) return;

        try {
          const timeInSeconds = timeStrToSeconds(time);
          if (timeInSeconds <= 0) return;

          const service = this.service();
          if (service?.serviceType === 'MINUTE') {
            this.form.patchValue(
              {
                price: +service.price * timeInSeconds,
                salePrice: +service.discountPrice * timeInSeconds,
                minutes: timeInSeconds,
              },
              { emitEvent: false }
            );
          } else {
            this.form.patchValue(
              { minutes: timeInSeconds },
              { emitEvent: false }
            );
          }

          this.emitFormValue();
        } catch {
          // Invalid time format
        }
      });

    // Emit form value on any change
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.initialized()) {
          this.emitFormValue();
        }
      });

    // Emit validity changes
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formValidChange.emit(this.form.valid);
      });
  }

  private emitFormValue(): void {
    const rawValue = this.form.getRawValue();
    this.formValueChange.emit(rawValue as BookingFormValue);
  }

  // ============ SERVICE SELECTION ============

  async chooseService(service: any): Promise<void> {
    if (!service) return;

    console.log(service);

    const emp = this.employee();
    if (!emp) return;

    // Try to get employee-specific service configuration
    let employeeService: any = null;
    if (service.employeeServices?.length > 0) {
      employeeService = service.employeeServices.find(
        (es: any) => es.employeeId === emp.id
      );
    }

    this.needChooseService.set(false);
    this.service.set(service);
    this.serviceChange.emit(service);

    // Set form values based on employee-service config or defaults
    if (employeeService) {
      const rawTime = employeeService.time
        ? secondsToTimeStr(employeeService.time)
        : service.time;
      // Normalize a meaningless "00:00" (often a default-saved value in DB) to
      // null so the field shows empty + required, instead of pre-filling
      // "00:00" which immediately fails ValidateTime and looks broken.
      const time = rawTime && rawTime !== '00:00' ? rawTime : null;

      this.form.patchValue(
        {
          serviceId: service.id,
          time,
          price: employeeService.price ?? service.price,
          salePrice: +service.discountPrice,
          salaryFixed: employeeService.fixedSalary,
          salaryPercent: employeeService.outputPercent,
          salaryPercentFromPrice: false,
          minutes: employeeService.time || timeStrToSeconds(service.time),
        },
        { emitEvent: false }
      );
    } else {
      this.form.patchValue(
        {
          serviceId: service.id,
          time: service.time && service.time !== '00:00' ? service.time : null,
          price: service.price,
          salePrice: +service.discountPrice,
          salaryFixed: null,
          salaryPercent: emp.outputPercent,
          salaryPercentFromPrice: false,
          minutes: timeStrToSeconds(service.time),
        },
        { emitEvent: false }
      );
    }

    // Set service products
    this.serviceProducts.set(
      this.mapServiceProducts(service.serviceProducts || [])
    );
    this.serviceProductsChange.emit(this.serviceProducts());

    // autoFix off → 0; autoFix+fixSum → fixSum; autoFix alone → products.
    // Do not touch additionalExpense — it is manual only (leave form value as-is).
    this.form.patchValue(
      {
        expense: seedAutoFixExpense(
          service.autoFix,
          service.fixSum,
          this.calculateTotalExpense()
        ),
      },
      { emitEvent: false }
    );

    this.emitFormValue();
  }

  async choosePackageService(
    packageService: AvailablePackageService
  ): Promise<void> {
    if (!packageService) return;

    const service = await firstValueFrom(
      this.serviceService.getById(packageService.serviceId)
    );

    await this.chooseService({
      ...service,
      packagePurchaseId: packageService.purchaseId,
      packageItemId: packageService.packageItemId,
      packageOriginalPrice: packageService.originalPrice,
      packageDiscountedPrice: packageService.discountedPrice,
    });

    // Keep package prices for display (total calculation excludes package services separately)
    this.form.patchValue(
      {
        price: packageService.originalPrice,
        salePrice: packageService.discountedPrice,
      },
      { emitEvent: false }
    );

    this.emitFormValue();
  }

  // ============ PRODUCTS & SPECS ============

  serviceProductsUpdate(products: any[]): void {
    this.serviceProducts.set(products);
    this.serviceProductsChange.emit(products);

    if (
      shouldTrackProductExpense(this.service()?.autoFix, this.service()?.fixSum)
    ) {
      this.form.patchValue({ expense: this.calculateTotalExpense() });
    }
  }

  serviceSpecsUpdate(specs: any[]): void {
    this.serviceSpecs.set(specs);
    this.specServicesChange.emit(specs);
  }

  // ============ ADDITIONAL EMPLOYEES ============

  addAdditionalEmployee(): void {
    this.additionalEmployeesForm.push(
      new FormGroup({
        employeeId: new FormControl<string | null>(null),
        salaryFixed: new FormControl<number | null>(null),
        // beaut-424: same ceiling as the main employee's share.
        salaryPercent: new FormControl<number | null>(null, [
          Validators.min(0),
          Validators.max(100),
        ]),
      })
    );
  }

  removeAdditionalEmployee(index: number): void {
    this.additionalEmployeesForm.removeAt(index);
  }

  async onAdditionalEmployeeChange(
    index: number,
    employeeId: string
  ): Promise<void> {
    if (!employeeId) return;

    try {
      const employees = this.employees();
      const employee = employees.find((e) => e.id === employeeId);

      if (employee) {
        const employeeFormGroup = this.additionalEmployeesForm.at(
          index
        ) as FormGroup;
        employeeFormGroup?.patchValue({
          salaryPercent: employee.outputPercent || null,
          salaryFixed: null,
        });
      }
    } catch {
      // Error loading employee
    }
  }

  // ============ UTILS ============

  changePercentage(percent: number | null): void {
    if (!percent) {
      this.form.patchValue({ salePrice: null });
      return;
    }
    const price = this.form.value.price;
    this.form.patchValue({
      salePrice:
        price && price > 0
          ? +(price - (price * percent) / 100).toFixed(2)
          : null,
    });
  }

  private mapServiceProducts(products: any[]): any[] {
    return (
      products?.map((m: any) => {
        let price = 0;
        if (m.product) {
          if (m.quantitySum > 0) {
            price = +m.product.salePricePerItem * +m.quantitySum;
          }
          if (m.grSum > 0) {
            price = +(
              (+m.product.salePricePerItem / +m.product.grPerItem) *
              +m.grSum
            ).toFixed(2);
          }
        } else {
          price = +m.priceValue || 0;
        }
        return {
          ...m,
          productId: m.product?.id || m.productId,
          productName: m.product?.name || m.productName || null,
          priceValue: price,
          price,
        };
      }) || []
    );
  }

  private calculateTotalExpense(): number {
    return this.serviceProducts().reduce(
      (total, item) => total + (+item.price || +item.priceValue || 0),
      0
    );
  }

  // ============ PUBLIC API ============

  /** Get current form value */
  getValue(): BookingFormValue {
    return this.form.getRawValue() as BookingFormValue;
  }

  /** Check if form is valid */
  isValid(): boolean {
    return this.form.valid;
  }

  /** Mark all controls as touched */
  markAllAsTouched(): void {
    this.form.markAllAsTouched();
  }

  /** Get current service */
  getService(): any {
    return this.service();
  }

  /** Get current service products */
  getServiceProducts(): any[] {
    return this.serviceProducts();
  }

  /** Get current spec services */
  getSpecServices(): any[] {
    return this.serviceSpecs();
  }
}
